import { Navigate, type RouteObject } from 'react-router-dom';
import { AuthenticatedShell, GuestOnly, RequireAuth } from '../components/auth/require-auth.js';
import { AppointmentsPage } from './appointments-page.js';
import { AttendancePage } from './attendance-page.js';
import { AttendanceSettingsPage } from './attendance-settings-page.js';
import { BillingPage } from './billing-page.js';
import { BoardPage } from './board-page.js';
import { CapacityPage } from './capacity-page.js';
import { CustomerDetailPage } from './customer-detail-page.js';
import { CustomerFormPage } from './customer-form-page.js';
import { CustomersPage } from './customers-page.js';
import { CustomersReportPage } from './customers-report-page.js';
import { ExpensesPage } from './expenses-page.js';
import { GalleryPage } from './gallery-page.js';
import { FinancialReportPage } from './financial-report-page.js';
import { ForgotPasswordPage } from './forgot-password-page.js';
import { HomeRoute } from './home-route.js';
import { ImportCustomersPage } from './import-customers-page.js';
import { IntegrationsPage } from './integrations-page.js';
import { FollowUpsPage } from './follow-ups-page.js';
import { QuotationsPage } from './quotations-page.js';
import { LoginPage } from './login-page.js';
import { CustomFieldsPage } from './custom-fields-page.js';
import { MeasurementTemplatesPage } from './measurement-templates-page.js';
import { MyEarningsPage } from './my-earnings-page.js';
import { MyWorkPage } from './my-work-page.js';
import { NewStockItemPage } from './new-stock-item-page.js';
import { NotFoundPage } from './not-found-page.js';
import { NotificationsPage } from './notifications-page.js';
import { BookOrderPage } from './book-order-page.js';
import { OrderDetailPage } from './order-detail-page.js';
import { OrdersPage } from './orders-page.js';
import { OutsourcingPage } from './outsourcing-page.js';
import { PaymentsPage } from './payments-page.js';
import { PayoutStatementPage } from './payout-statement-page.js';
import { PayoutsPage } from './payouts-page.js';
import { PnlPage } from './pnl-page.js';
import { GarmentsPage } from './garments-page.js';
import { ProductsPage } from './products-page.js';
import { PurchasingPage } from './purchasing-page.js';
import { SourcingPage } from './sourcing-page.js';
import { ProductionReportPage } from './production-report-page.js';
import { StaffPayoutPage } from './staff-payout-page.js';
import { RateCardPage } from './rate-card-page.js';
import { ReportsPage } from './reports-page.js';
import { SalesRegisterPage } from './sales-register-page.js';
import { SalesReportPage } from './sales-report-page.js';
import { SecurityPage } from './security-page.js';
import { DeliverySchedulesPage } from './delivery-schedules-page.js';
import { AlterationsPage } from './alterations-page.js';
import { AlterationDetailPage } from './alteration-detail-page.js';
import { SupportPage } from './support-page.js';
import { CouponsPage } from './coupons-page.js';
import { ShopDetailsPage } from './shop-details-page.js';
import { ShopPresentationPage } from './shop-presentation-page.js';
import { FormLayoutsPage } from './form-layouts-page.js';
import { StageFlowsPage } from './stage-flows-page.js';
import { WorkflowSettingsPage } from './workflow-settings-page.js';
import { SettingsPage } from './settings-page.js';
import { SetupPage } from './setup-page.js';
import { SignupPage } from './signup-page.js';
import { StaffDetailPage } from './staff-detail-page.js';
import { StaffJoinPage } from './staff-join-page.js';
import { StaffPage } from './staff-page.js';
import { StockGroupsPage } from './stock-groups-page.js';
import { StockItemPage } from './stock-item-page.js';
import { StockPage } from './stock-page.js';
import { VendorPage } from './vendor-page.js';
import { StaffReportPage } from './staff-report-page.js';

/**
 * Two trees, one router.
 *
 * The auth screens sit OUTSIDE the shell — no sidebar, no bottom navigation, nothing to
 * navigate to — and everything else sits inside `RequireAuth`, so "is this person signed in?"
 * is answered once, by the route table, rather than by each screen remembering to check.
 *
 * Every private screen hangs off the one layout route, so the chrome mounts once and survives
 * navigation: the connection banner does not blink and the write queue is not rebuilt between
 * screens.
 *
 * Exported as plain route objects rather than a built router so tests can mount the same tree
 * inside a memory router.
 */
export const routes: RouteObject[] = [
  {
    path: '/login',
    element: (
      <GuestOnly>
        <LoginPage />
      </GuestOnly>
    ),
  },
  {
    path: '/signup',
    element: (
      <GuestOnly>
        <SignupPage />
      </GuestOnly>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <GuestOnly>
        <ForgotPasswordPage />
      </GuestOnly>
    ),
  },
  {
    // An invited karigar arrives here with no account at all, so it sits outside the shell
    // beside the other credential screens — and ends inside the shop that invited them.
    path: '/join',
    element: (
      <GuestOnly>
        <StaffJoinPage />
      </GuestOnly>
    ),
  },

  {
    // The bare root is private like every other path. The marketing landing is a separate
    // static site on its own domain (apps/landing), so a stranger who reaches the app host
    // gets the sign-in screen, and a signed-in shop gets `HomeRoute`.
    path: '/',
    element: (
      <RequireAuth>
        <AuthenticatedShell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <HomeRoute /> },
      { path: 'board', element: <BoardPage /> },
      // The karigar's own screen. Routed for every role — a small shop's owner does the
      // finishing themselves — but it is only in the navigation for a tailor, whose whole
      // product it is.
      { path: 'my-work', element: <MyWorkPage /> },
      // The karigar's own wages. Routed for every role for the same reason `my-work` is — and
      // it is the only earnings screen a tailor can reach at all.
      { path: 'my-earnings', element: <MyEarningsPage /> },
      { path: 'orders', element: <OrdersPage /> },
      // `new` before `:orderId`, or the word would be read as an id.
      { path: 'orders/new', element: <BookOrderPage /> },
      { path: 'orders/:orderId', element: <OrderDetailPage /> },
      { path: 'customers', element: <CustomersPage /> },
      // Leads are gone — customers are the only contact record. The old addresses redirect
      // rather than 404 because they may be in somebody's history or pasted into a chat. A
      // migrated lead's id is not a customer id, so `/leads/:id` lands on the list, not a record.
      // Follow-ups have their own screen now and keep their target.
      { path: 'leads', element: <Navigate to="/customers" replace /> },
      { path: 'leads/follow-ups', element: <Navigate to="/follow-ups" replace /> },
      { path: 'leads/:leadId', element: <Navigate to="/customers" replace /> },
      // The pipeline board and deals are retired — a boutique sells walk-in → quotation → order.
      // Their old addresses land on the customer book rather than 404ing.
      { path: 'pipeline', element: <Navigate to="/customers" replace /> },
      { path: 'deals', element: <Navigate to="/customers" replace /> },
      { path: 'leads/pipeline', element: <Navigate to="/customers" replace /> },
      // What one person owes today, across every customer. It exists because a task's OWNER and
      // a customer's ASSIGNEE may differ, so the customer list — which filters by assignee —
      // cannot answer "what am I doing today".
      { path: 'follow-ups', element: <FollowUpsPage /> },
      // Every price the shop has out, across customers. The document itself belongs to a
      // customer and is worked on there; this is the list that answers "what have we quoted, and
      // what is still waiting on an answer" — a question no single customer can be asked.
      { path: 'quotations', element: <QuotationsPage /> },
      // The week's fittings and delivery handovers (task 15). Beside quotations rather than
      // under any single customer or order: a boutique opens this the way it opens a diary, not
      // by way of one customer's record.
      { path: 'appointments', element: <AppointmentsPage /> },
      // "Can we have it by the 24th?" — the workshop's book, asked before anybody promises. Its
      // own screen beside the board rather than a panel inside booking: the question is asked
      // over the phone as often as it is asked at the counter, with no order to hang it on.
      { path: 'capacity', element: <CapacityPage /> },
      // The shop's design library, browsed as folders. Beside quotations rather than under
      // orders: a folder like Reference or Product is not scoped to any one booking.
      { path: 'gallery', element: <GalleryPage /> },
      // One order's designs, agreed in pictures. Its own URL so a board can be linked and comes
      // back the way it was left — see `?orderId=`.
      // Bringing the list they already have. Under `customers` rather than under settings,
      // because it is reached from the empty customer book — which is where a new shop is
      // standing when it first wants it. `import` before any id route, or it reads as one.
      { path: 'customers/import', element: <ImportCustomersPage /> },
      // Adding somebody, on a page of its own. Above `:customerId` for the same reason `import`
      // is — `new` would otherwise be read as an id. The dialog it replaces is still what the
      // booking form opens for a walk-in mid-order; see `customer-form-dialog.tsx`.
      { path: 'customers/new', element: <CustomerFormPage /> },
      // One customer and everything the shop has done for them. A page rather than the dialog it
      // used to be, so it can be linked to from a deal card or a measurements band, and so its
      // working surfaces get the width they need. Below `import`, or that word reads as an id.
      { path: 'customers/:customerId', element: <CustomerDetailPage /> },
      // Correcting one. Below the detail page rather than above it, because `:customerId` here
      // is a real id either way and the longer path is the more specific match.
      { path: 'customers/:customerId/edit', element: <CustomerFormPage /> },
      // Setting the shop up. Its own screen rather than a modal over the dashboard: an owner
      // gets interrupted halfway through and has to be able to come back to a URL.
      { path: 'setup', element: <SetupPage /> },
      // Not in the bottom navigation — it has five slots and no sixth. Reached from the order
      // book, which is where a shop already is when it starts thinking about money.
      { path: 'payments', element: <PaymentsPage /> },
      // Money out. Beside payments rather than under reports: recording what the shop spent is
      // counter work a manager does, not a report an owner reads.
      { path: 'expenses', element: <ExpensesPage /> },
      // Wages. Beside expenses, because paying the karigars is the other half of money going
      // out — `staff/:staffId` before `:payoutId`, or the word would be read as an id.
      { path: 'payouts', element: <PayoutsPage /> },
      { path: 'payouts/staff/:staffId', element: <StaffPayoutPage /> },
      { path: 'payouts/:payoutId', element: <PayoutStatementPage /> },
      /**
       * Fabric and trims (task 27). Its own top-level route rather than a corner of settings:
       * stock is counter work somebody does several times a day.
       */
      /**
       * The price list. Its own top-level route beside stock rather than under settings: what a
       * blouse costs is looked up at the counter mid-quote, not configured twice a year — and it
       * is deliberately NOT under `/stock`, because a catalogue holds no quantity and a shelf
       * holds no selling price.
       */
      { path: 'products', element: <ProductsPage /> },
      /**
       * What the shop MAKES, and what it charges to make one. Beside the price list rather than
       * under settings for the same reason that is: a blouse's price is looked up at the counter
       * mid-quote, not configured twice a year. The measurement FIELDS of the same garment stay
       * on `/settings/measurements` — see `garments-page.tsx` for why the two are split.
       */
      { path: 'garments', element: <GarmentsPage /> },
      { path: 'stock', element: <StockPage /> },
      // `new` before `:itemId`, or the word would be read as an id.
      { path: 'stock/new', element: <NewStockItemPage /> },
      // The headings the shelf is read under. Before `:itemId` for the same reason `new` is.
      { path: 'stock/groups', element: <StockGroupsPage /> },
      { path: 'stock/:itemId', element: <StockItemPage /> },
      /**
       * Work sent outside (task 29). Beside stock rather than under it: an embroiderer is not a
       * material, and what this tracks is where the shop's own pieces physically are.
       */
      /**
       * Buying (task 28). Beside stock rather than inside it: a different question — what was
       * ordered and what is owed, as against what is on the shelf.
       */
      { path: 'purchasing', element: <PurchasingPage /> },
      /**
       * What each ORDER needs, as against what was bought or what is on the shelf. Beside
       * purchasing rather than under it: a requirement is a need somebody has to go and satisfy,
       * and the question "what are we waiting on, across every job?" belongs to no single order.
       */
      { path: 'sourcing', element: <SourcingPage /> },
      { path: 'outsourcing', element: <OutsourcingPage /> },
      { path: 'outsourcing/vendors/:vendorId', element: <VendorPage /> },
      { path: 'reports', element: <ReportsPage /> },
      // The five reports (task 26). Each is its own URL rather than a tab, because "send me the
      // production numbers" is a link somebody pastes into WhatsApp.
      { path: 'reports/sales', element: <SalesReportPage /> },
      // The pipeline report went with deals.
      { path: 'reports/pipeline', element: <Navigate to="/reports" replace /> },
      { path: 'reports/production', element: <ProductionReportPage /> },
      { path: 'reports/staff', element: <StaffReportPage /> },
      { path: 'reports/customers', element: <CustomersReportPage /> },
      { path: 'reports/financial', element: <FinancialReportPage /> },
      // The profit view IS a report, and the owner's alone — see the permission matrix.
      { path: 'reports/profit-and-loss', element: <PnlPage /> },
      // The filing-time report. It sits under reports rather than under settings because it is
      // something a shop READS about its year, not something it configures.
      { path: 'reports/sales-register', element: <SalesRegisterPage /> },
      { path: 'staff', element: <StaffPage /> },
      { path: 'staff/:staffId', element: <StaffDetailPage /> },
      // Who is in the shop today. Routed for every role, like `my-work` is: the API answers a
      // karigar with their own row, and "am I marked in?" is a question they are entitled to ask.
      { path: 'attendance', element: <AttendancePage /> },
      { path: 'settings', element: <SettingsPage /> },
      // Name, phone, address and GST — the top of every bill.
      { path: 'settings/shop', element: <ShopDetailsPage /> },
      // Which stages the shop uses and what it calls them.
      { path: 'settings/workflow', element: <WorkflowSettingsPage /> },
      // The shop-wide rate card is a fact about the business, so it sits under Settings; a
      // person's own overrides live on their staff page.
      { path: 'settings/delivery-schedules', element: <DeliverySchedulesPage /> },
      { path: 'settings/stage-flows', element: <StageFlowsPage /> },
      { path: 'alterations', element: <AlterationsPage /> },
      { path: 'alterations/:alterationId', element: <AlterationDetailPage /> },
      // Help. A top-level route rather than one under Settings, and routed for every role: the
      // person most likely to be stuck is the one with the fewest permissions, and a help
      // screen behind a settings menu is one nobody finds while they are stuck.
      { path: 'support', element: <SupportPage /> },
      { path: 'settings/rates', element: <RateCardPage /> },
      // The shop's own coupon codes. Under Settings because setting one up is a standing
      // commitment an owner makes a few times a year — typing one somebody read off a slip
      // happens at the counter, on the booking form, where the code is actually used.
      { path: 'settings/coupons', element: <CouponsPage /> },
      // Where to pay, how orders are numbered, and what mark goes on the shop's photographs.
      { path: 'settings/presentation', element: <ShopPresentationPage /> },
      // What each form asks for. Shop-wide, unlike the column preferences each person keeps.
      { path: 'settings/form-layouts', element: <FormLayoutsPage /> },
      // What the shop pays and what that includes. Under Settings rather than in the primary
      // navigation: an owner visits it twice a year, and every refusal that matters links
      // straight here from the point of refusal.
      { path: 'settings/billing', element: <BillingPage /> },
      { path: 'settings/measurements', element: <MeasurementTemplatesPage /> },
      // The old enquiry-form settings address; its fields now live with the custom fields.
      { path: 'settings/leads', element: <Navigate to="/settings/custom-fields" replace /> },
      // The pipeline editor went with deals.
      { path: 'settings/pipelines', element: <Navigate to="/settings" replace /> },
      { path: 'settings/custom-fields', element: <CustomFieldsPage /> },
      // The shop's workday window, which is what gives `late` and `halfDay` their meaning. Under
      // Settings rather than on the attendance screen: it is edited twice and read every day.
      { path: 'settings/attendance', element: <AttendanceSettingsPage /> },
      // The outside services the shop is wired to. Beside the messaging screen rather than
      // inside it: connecting WhatsApp is a setup question asked twice, and what customers are
      // told is a question edited for months.
      { path: 'settings/integrations', element: <IntegrationsPage /> },
      { path: 'settings/notifications', element: <NotificationsPage /> },
      { path: 'settings/security', element: <SecurityPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];
